/**
 * Display value formatting for card/detail components.
 *
 * Maps `FormFieldMeta` + raw value → formatted ReactNode for display
 * in DetailCard, DetailGridCard, and any read-only context.
 *
 * This is the display-side counterpart to `MetadataField` (edit-side).
 * Both use `FormFieldMeta` as their field contract, ensuring the same
 * field definition drives both rendering and editing.
 *
 * Usage:
 *   import { formatDisplayValue } from '@/shared/lib/forms'
 *
 *   <span>{formatDisplayValue(field, value, { locale, currency })}</span>
 */

import type { ReactNode } from 'react'
import type { FormFieldMeta, SelectOption } from './types'
import { inferReviewFormat } from './types'
import {
  addressDisplay,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  warnMissingCurrency,
} from '@/shared/lib/formatting'
import { formatPhoneDisplay, splitStoredValue } from './phone'
import { resolveDependentOptions } from './normalise'
import { titleCaseKey } from '@/shared/lib/humanise'

export interface DisplayFormatOptions {
  /** BCP 47 locale tag (e.g. 'en-GB'). */
  locale?: string
  /** ISO 4217 currency code (e.g. 'GBP'). */
  currency?: string
  /** Sibling values, so a dependent SELECT reads its parent's option group. */
  values?: Record<string, unknown>
}

const EMPTY = '-'

/**
 * Format a field value as a ReactNode for display in cards/detail views.
 *
 * Returns a string or ReactNode. Handles all canonical field types:
 * dates, currencies, numbers, booleans, selects, files, links, etc.
 */
/**
 * A stored value as display text. Picker-era values are objects
 * (`{ uuid, name }`); show their readable part rather than "[object Object]".
 */
export function displayScalar(value: unknown): string {
  if (value !== null && Object.prototype.toString.call(value) === '[object Object]') {
    const record = value as Record<string, unknown>
    for (const key of ['name', 'label', 'display', 'value']) {
      const part = record[key]
      if (typeof part === 'string' || typeof part === 'number') return String(part)
    }
    return ''
  }
  return String(value ?? '')
}

export function formatDisplayValue(
  field: FormFieldMeta,
  value: unknown,
  options?: DisplayFormatOptions,
): ReactNode {
  if (value === undefined || value === null || value === '') {
    return EMPTY
  }

  const locale = options?.locale ?? DEFAULT_LOCALE
  const currency = options?.currency ?? DEFAULT_CURRENCY
  const format = inferReviewFormat(field)

  switch (format) {
    case 'boolean':
      // Accept booleans and Select-submitted 'yes'/'no' strings.
      return value === true || value === 'true' || value === 'yes' ? 'Yes' : 'No'

    case 'date':
      return fmtDate(value, locale)

    case 'datetime':
      return fmtDateTime(value, locale)

    case 'currency':
      if (options?.currency === undefined) {
        warnMissingCurrency('formatDisplayValue', field.code_name)
      }
      return fmtCurrency(value, currency, locale)

    case 'number':
      return fmtNumber(value, locale)

    case 'select':
      return resolveOptionLabel(displayScalar(value), field, options?.values)

    case 'multi_select': {
      // Backend may return either an array or a comma-separated string
      // (e.g. "ice_water_barrier,starter,underlayment"). Normalise to a
      // string array so option labels resolve in both cases.
      const items = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? value.split(',').map((v) => v.trim()).filter(Boolean)
          : []
      if (items.length === 0) return EMPTY
      return items
        .map((v) => resolveOptionLabel(displayScalar(v), field, options?.values))
        .join(', ')
    }

    case 'file':
      return renderFile(value)
    case 'file_count':
      return fmtFile(value)

    case 'address':
      return fmtAddress(value)

    case 'entity_search':
      return fmtEntityReference(value)

    case 'phone': {
      const raw = String(value)
      const { base } = splitStoredValue(raw)
      return renderContactLink(`tel:${base}`, formatPhoneDisplay(raw))
    }

    case 'email':
      return renderContactLink(`mailto:${String(value)}`, String(value))

    default:
      if (field.options?.length && typeof value === 'string') {
        return resolveOptionLabel(value, field, options?.values)
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const record = value as Record<string, unknown>
        if (
          ('amount' in record || 'currency' in record) &&
          Object.keys(record).every((k) => k === 'amount' || k === 'currency')
        ) {
          return fmtCurrency(value, currency, locale)
        }
      }
      if (typeof value === 'string' && value.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(value) as Record<string, unknown>
          if (
            parsed && typeof parsed === 'object' && !Array.isArray(parsed) &&
            ('amount' in parsed || 'currency' in parsed) &&
            Object.keys(parsed).every((k) => k === 'amount' || k === 'currency')
          ) {
            return fmtCurrency(parsed, currency, locale)
          }
        } catch { /* not JSON */ }
      }
      return String(value)
  }
}

// Click-to-call / click-to-email. stopPropagation keeps a click on the link
// from also firing the row's own click handler in tables.
function renderContactLink(href: string, label: string): ReactNode {
  return (
    <a
      href={href}
      onClick={(event) => event.stopPropagation()}
      className="text-[var(--green)] underline-offset-2 hover:underline"
    >
      {label}
    </a>
  )
}

/**
 * Check whether a value is considered "empty" for display purposes.
 */
export function isDisplayEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0)
}

// ── Helpers ─────────────────────────────────────────────────

function fmtDate(value: unknown, locale: string): string {
  if (!value) return EMPTY
  try {
    const iso = String(value)
    const dateStr = iso.includes('T') ? iso : iso + 'T12:00:00'
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return String(value)
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d)
  } catch {
    return String(value)
  }
}

function fmtDateTime(value: unknown, locale: string): string {
  if (!value) return EMPTY
  try {
    const d = new Date(String(value))
    if (isNaN(d.getTime())) return String(value)
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  } catch {
    return String(value)
  }
}

function fmtCurrency(value: unknown, currencyCode: string, locale: string): string {
  // Money fields can arrive as a { amount, currency } object (or its
  // JSON-string form) rather than a bare number. Unwrap it so we format
  // the amount and never echo the raw dict - an absent amount is empty.
  const resolvedCurrency = currencyCode
  let moneyObj: Record<string, unknown> | null = null
  if (typeof value === 'string' && value.trim().startsWith('{')) {
    try {
      moneyObj = JSON.parse(value) as Record<string, unknown>
    } catch {
      moneyObj = null
    }
  } else if (value && typeof value === 'object' && !Array.isArray(value)) {
    moneyObj = value as Record<string, unknown>
  }
  if (moneyObj) {
    const amount = moneyObj.amount
    if (amount === null || amount === undefined || amount === '') return EMPTY
    // The caller-supplied currency (the tenant/organisation's configured
    // currency) is authoritative; any currency baked into the stored value is
    // ignored so all money follows the tenant's currency choice.
    value = amount
  }
  const num = typeof value === 'number' ? value : Number(value)
  if (isNaN(num)) return String(value)
  try {
    const hasDecimals = num % 1 !== 0
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: resolvedCurrency,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    }).format(num)
  } catch {
    return `${resolvedCurrency} ${num}`
  }
}

function fmtNumber(value: unknown, locale: string): string {
  const num = typeof value === 'number' ? value : Number(value)
  if (isNaN(num)) return String(value)
  try {
    return new Intl.NumberFormat(locale).format(num)
  } catch {
    return String(num)
  }
}

/**
 * A choice value's label: the field's own options, then the option group its
 * parent value selects (a value can carry a different label per group), then
 * any group, then the humanised value.
 */
function resolveOptionLabel(
  value: string,
  field: FormFieldMeta,
  values?: Record<string, unknown>,
): string {
  const labelIn = (options?: SelectOption[]) =>
    Array.isArray(options)
      ? options.find((o) => String(o?.value) === value)?.label
      : undefined
  return (
    labelIn(field.options) ||
    labelIn(resolveDependentOptions(field.configuration_data, values ?? {})) ||
    findOptionLabel(field.configuration_data, value) ||
    titleCaseKey(value)
  )
}

/**
 * The label a choice field's ``configuration_data`` gives a stored value,
 * searching ``options`` and nested ``option_groups``. ``null`` when absent.
 */
export function findOptionLabel(config: unknown, value: string, depth = 0): string | null {
  if (!config || typeof config !== 'object' || depth > 2) return null
  const { options, option_groups: groups } = config as {
    options?: unknown
    option_groups?: unknown
  }
  const lists: unknown[] = [options]
  if (groups && typeof groups === 'object') lists.push(...Object.values(groups))
  for (const list of lists) {
    if (Array.isArray(list)) {
      for (const opt of list) {
        const o = opt as { value?: unknown; label?: unknown }
        if (o?.value === value && typeof o.label === 'string') return o.label
      }
    } else {
      const nested = findOptionLabel(list, value, depth + 1)
      if (nested) return nested
    }
  }
  return null
}


function fmtAddress(value: unknown): string {
  const display = addressDisplay(value)
  return display || EMPTY
}

/**
 * Render an ENTITY_SEARCH value (a picked resource reference, e.g. a
 * dealer/retailer partnership) as its human-readable label. The picker stores
 * a composite object like `{ uuid, name }` (or `{ uuid: '', name }` for a
 * free-text "add" entry); without this it would stringify to "[object Object]".
 */
function fmtEntityReference(value: unknown): string {
  // The picked reference can arrive as its JSON-string form (e.g. a HOLDING EAV
  // value stored as `{"uuid":"...","name":"..."}`) rather than a parsed object.
  // Unwrap it so we surface the human-readable name, not the raw dict - a plain
  // (non-JSON) string is already the label.
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return fmtEntityReference(parsed)
        }
      } catch {
        /* not JSON - fall through to the plain-string label */
      }
    }
    return value || EMPTY
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>
    const label = [obj.display, obj.name, obj.label].find(
      (v): v is string => typeof v === 'string' && v.trim().length > 0,
    )
    if (label) return label
    // Composite display column (e.g. `partnership__name`) when no top-level
    // name/label is present.
    const dunder = Object.entries(obj).find(
      ([k, v]) => k.includes('__') && typeof v === 'string' && v.trim().length > 0,
    )
    if (dunder) return dunder[1] as string
  }
  return EMPTY
}

function fmtFile(value: unknown): string {
  if (typeof value === 'string') return value || EMPTY
  if (Array.isArray(value)) {
    const count = value.length
    if (count === 0) return EMPTY
    return `${count} file${count === 1 ? '' : 's'}`
  }
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>
    if (obj.display && typeof obj.display === 'string') return obj.display
    if (obj.file_name) return String(obj.file_name)
    const nested = obj.value as Record<string, unknown> | undefined
    if (nested?.file_name) return String(nested.file_name)
    if (obj.uuid) return 'Uploaded'
    return EMPTY
  }
  return EMPTY
}

/**
 * Render a single file/photo envelope as an underlined link that opens the
 * file in a new tab. Returns `null` when the value is not a resolvable
 * single-file envelope so the caller can fall back.
 */
function renderFileLink(value: unknown, key?: string): ReactNode {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const obj = value as Record<string, unknown>
  const inner =
    obj.value && typeof obj.value === 'object'
      ? (obj.value as Record<string, unknown>)
      : obj
  const url = (inner.file_url ?? inner.fileUrl) as string | undefined
  const label =
    [obj.display, inner.file_name, inner.fileName].find(
      (v): v is string => typeof v === 'string' && v.length > 0,
    ) ?? 'Document'
  if (!url) return null
  return (
    <a
      key={key}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="text-[var(--green)] underline-offset-2 hover:underline"
    >
      {label}
    </a>
  )
}

/**
 * Render a file/photo attribute value as a link (or list of links) that
 * opens each file in a new tab. Handles a single envelope and an array of
 * envelopes (multi-photo). Falls back to the plain-text label (`fmtFile`)
 * for legacy string values or envelopes without a resolvable URL.
 */
function renderFile(value: unknown): ReactNode {
  if (Array.isArray(value)) {
    const links = value
      .map((item, index) => renderFileLink(item, `file-${index}`))
      .filter((node): node is ReactNode => node !== null)
    if (links.length === 0) return fmtFile(value)
    return links.map((node, index) => (
      <span key={index} className="ml-2 first:ml-0">
        {node}
      </span>
    ))
  }
  return renderFileLink(value) ?? fmtFile(value)
}
